import { AttachmentIDType } from '@policyfly/protobuf'
import { AttachmentDetail, AttachmentDetail_SignatureStatus, AttachmentVisibility } from '@policyfly/protobuf/patrick'
import { generateUUID } from '@policyfly/utils/uuid'
import { defineStore } from 'pinia'
import { markRaw } from 'vue'

import { useApiStore } from '@/stores/api'
import { useAppContextStore } from '@/stores/appContext'
import { useFeedStore } from '@/stores/feed'
import { useNotificationStore } from '@/stores/notification'
import { useSettingsStore } from '@/stores/settings'

import { useDocReviewStore } from './docReview'
import { api } from '@/api'
import { isAbortError } from '@/utils/api'
import {
  attachmentFieldLocations,
  contentLocationField,
  isContentLoaded,
  loadContentLocation,
  preserveAttachmentContent,
  preserveAttachmentListContent,
  revokeAttachmentContent,
} from '@/utils/attachments'
import { hotReloadStore } from '@/utils/build'
import { ATTACHMENT_PENDING_KEY, stringToEnumAttachmentCategory } from '@/utils/protobuf'

import type { UploadMeta } from './api/endpoints/attachment'
import type { AttachmentDetail_ContentLocation, AttachmentDetail_ContentLocation_Field } from '@policyfly/protobuf/patrick'
import type { SignableDocument, SignableDocuments } from 'types/application'

export interface AttachmentState {
  /**
   * Attachments that have been added but not yet uploaded
   */
  allPendingAttachments: AttachmentDetail[]
  allApplicationAttachments: AttachmentDetail[]
  allPolicyAttachments: AttachmentDetail[]
  isListing: boolean
  /**
   * Content currently being read, keyed by attachment id and field,
   * so a field asked for again while loading shares the read rather than starting another.
   */
  contentLoads: Map<string, Promise<AttachmentDetail_ContentLocation>>
}

export const ATTACHMENT_UPLOAD_PREFIX = 'uploading:'

export const useAttachmentStore = defineStore('attachment', {
  state: (): AttachmentState => ({
    allPendingAttachments: [],
    allApplicationAttachments: [],
    allPolicyAttachments: [],
    isListing: false,
    contentLoads: markRaw(new Map()),
  }),

  getters: {
    pendingAttachments (): AttachmentDetail[] {
      return this.allPendingAttachments
    },
    applicationAttachments (): AttachmentDetail[] {
      return this.allApplicationAttachments
    },
    policyAttachments (): AttachmentDetail[] {
      return this.allPolicyAttachments
    },
    uploadedAttachments (): AttachmentDetail[] {
      return this.applicationAttachments.filter((a) => !a.policyDocument)
    },
    /**
     * Finds the instance of an attachment currently listed.
     * Refreshing a list replaces its instances, so one held from before may no longer be listed.
     */
    listedAttachment (): (id: string) => AttachmentDetail | undefined {
      return (id) => this.allApplicationAttachments.find((a) => a.id === id) ??
        this.allPolicyAttachments.find((a) => a.id === id)
    },
    unconfirmedSignatures (): boolean {
      const appContextStore = useAppContextStore()
      if (appContextStore.status === 'TERMINATED') return false
      return this.allApplicationAttachments.some((att) => {
        return !att.archived && att.signatureStatus === AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_SIGNED
      })
    },
    /**
     * @returns {SignableDocuments | null} Signable Documents Settings along with any signable documents or null
     */
    signableDocuments (): SignableDocuments | null {
      const settingsStore = useSettingsStore()
      const signableDocuments = settingsStore.signableDocuments
      if (!signableDocuments?.documents?.length) return null
      if (!this.allApplicationAttachments) return null
      return {
        ...signableDocuments,
        documents: signableDocuments.documents.reduce<SignableDocument[]>((acc, d) => {
          const { documentType } = d
          const attachment = this.applicationAttachments.find((att) => {
            return !att.archived && att.documentType === documentType
          })
          if (attachment) {
            acc.push({
              attachment,
              settings: d,
            })
          }
          return acc
        }, []),
      }
    },
  },

  actions: {
    /**
     * Hard archives an attachment, removing it from the list.
     */
    async archive ({ id }: { id: string | number }): Promise<void> {
      const feedStore = useFeedStore()
      const apiStore = useApiStore()

      await apiStore.attachment.archive(id, false)

      this.removeFile(id)
      await feedStore.refresh()
    },
    /**
     * Lists the attachments for the loaded application or policy.
     * Preserves content URLs from the previous list.
     *
     * Pass an `abortController` to be able to cancel the request.
     * A cancelled list does not reset loading as it assumes the caller has scheduled another list.
     */
    async list ({ policy, includeHistory, abortController }: { policy?: boolean, includeHistory?: boolean, abortController?: AbortController } = {}): Promise<void> {
      const apiStore = useApiStore()
      const appContext = useAppContextStore()
      const notificationStore = useNotificationStore()
      const docReviewStore = useDocReviewStore()

      try {
        this.isListing = true
        const attachments = policy
          ? await apiStore.attachment.list(appContext.policyId!, AttachmentIDType.ID_Type_Policy, abortController)
          : await apiStore.attachment.list(appContext.applicationId!, AttachmentIDType.ID_Type_Application, abortController)

        policy
          ? (this.allPolicyAttachments = preserveAttachmentListContent(attachments, this.allPolicyAttachments, this.allApplicationAttachments))
          : (this.allApplicationAttachments = preserveAttachmentListContent(attachments, this.allApplicationAttachments, this.allPolicyAttachments))

        if (includeHistory && attachments.length) {
          await docReviewStore.list(attachments.map(({ id }) => id))
        }
      } catch (error) {
        if (isAbortError(error)) return
        console.error('Error listing attachments:', error)
        notificationStore.snackbar.error('Error loading attachments')
      } finally {
        if (!abortController?.signal.aborted) {
          this.isListing = false
        }
      }
    },
    /**
     * @internal
     * Loads the content an attachment holds in a field into every location on it that points there.
     * Only that field is read, leaving the attachment's other content as it is.
     *
     * Only called by {@link loadLocation}, which consumers should use instead as it skips content that is
     * already loaded or has no field to load from.
     *
     * A field already being read for the attachment shares that read. The content is also loaded into the
     * listed instance of the attachment, as a list refreshed while reading replaces the one passed in.
     * Locations that are already loaded are left alone, so a url in use is never revoked.
     */
    async loadContent ({ attachment, field }: { attachment: AttachmentDetail, field: AttachmentDetail_ContentLocation_Field }): Promise<void> {
      const key = `${attachment.id}:${field}`
      let reading = this.contentLoads.get(key)
      if (!reading) {
        const apiStore = useApiStore()
        reading = apiStore.attachment.readContent(attachment.id, field)
        this.contentLoads.set(key, reading)
        const settle = (): void => { this.contentLoads.delete(key) }
        reading.then(settle, settle)
      }
      const content = await reading
      for (const target of new Set([attachment, this.listedAttachment(attachment.id)])) {
        if (!target) continue
        for (const location of attachmentFieldLocations(target, field)) {
          if (!isContentLoaded(location)) loadContentLocation(location, content)
        }
      }
    },
    /**
     * Loads the field a location of the attachment points at, unless it is already loaded
     * or has no field to load from. See {@link loadContent}.
     *
     * @returns The listed instance of the attachment if there is one, otherwise the attachment passed in.
     * A list refreshed while loading replaces the instance passed in, so read the content from this one.
     */
    async loadLocation ({ attachment, location }: { attachment: AttachmentDetail, location: AttachmentDetail_ContentLocation | undefined }): Promise<AttachmentDetail> {
      const field = contentLocationField(location)
      if (field !== undefined && !isContentLoaded(location)) await this.loadContent({ attachment, field })
      return this.listedAttachment(attachment.id) ?? attachment
    },
    async relocate ({ id, group }: { id: string | number, group: string }): Promise<void> {
      const apiStore = useApiStore()
      this.replaceAttachment(await apiStore.attachment.relocate(id, group))
    },
    async bulkRelocate ({ ids, group }: { ids: Array<string | number>, group: string }): Promise<void> {
      const apiStore = useApiStore()
      await apiStore.attachment.bulkRelocate(ids, group)
    },
    async rename ({ id, name }: { id: string | number, name: string }): Promise<void> {
      const feedStore = useFeedStore()
      const apiStore = useApiStore()

      this.replaceAttachment(await apiStore.attachment.rename(id, name))
      await feedStore.refresh()
    },
    async remove ({ id }: { id: string | number }): Promise<void> {
      const feedStore = useFeedStore()
      const apiStore = useApiStore()

      await apiStore.attachment.delete(id)

      this.removeFile(id)
      await feedStore.refresh()
    },
    async restore ({ id }: { id: string | number }): Promise<void> {
      const feedStore = useFeedStore()
      const apiStore = useApiStore()

      this.replaceAttachment(await apiStore.attachment.restore(id))
      await feedStore.refresh()
    },
    async setVisibility ({ id, restricted_visibility }: { id: string | number, restricted_visibility: boolean }): Promise<void> {
      const feedStore = useFeedStore()
      const apiStore = useApiStore()

      this.replaceAttachment(await apiStore.attachment.updateVisibility(
        id,
        // eslint-disable-next-line camelcase
        restricted_visibility
          ? AttachmentVisibility.ATTACHMENT_VISIBILITY_RESTRICTED
          : AttachmentVisibility.ATTACHMENT_VISIBILITY_UNRESTRICTED,
      ))
      await feedStore.refresh()
    },
    async softArchive ({ id }: { id: string | number }): Promise<void> {
      const feedStore = useFeedStore()
      const apiStore = useApiStore()

      // a soft archive always returns the attachment, only a permanent one does not
      this.replaceAttachment((await apiStore.attachment.archive(id, true))!)
      await feedStore.refresh()
    },
    /**
     * Validates a file against existing attachments.
     *
     * @param file The file to validate
     * @param policyDocument Whether the file is a policy document
     * @returns Whether the file is valid or the user has bypassed validation
     */
    async validate ({ file, policyDocument }: { file: File, policyDocument?: boolean }): Promise<boolean> {
      const notificationStore = useNotificationStore()
      const attachments = policyDocument
        ? this.policyAttachments
        : this.uploadedAttachments
      if (attachments.find((a) => a.filename === file.name)) {
        if (!await notificationStore.confirm({ title: `The file "${file.name}" is already uploaded. Are you sure you'd like to provide a duplicate?` })) {
          return false
        }
      }
      return true
    },
    /**
     * Uploads a file to the server, adds it to the attachments list and event to feed.
     *
     * @param file The file to upload
     * @param meta Metadata for the attachment
     * @param fuzzy Whether to save as fuzzy, supported for Django programs only
     * @param validate Whether to validate the file against existing attachments
     */
    async upload ({ file, meta, fuzzy, validate }: {
      file: File
      meta: UploadMeta
      fuzzy?: boolean
      validate?: boolean
    }): Promise<AttachmentDetail | undefined> {
      try {
        const apiStore = useApiStore()
        const feedStore = useFeedStore()

        if (validate && !await this.validate({ file, policyDocument: meta.policyDocument })) return
        const attachment = await apiStore.attachment.upload(file, meta, fuzzy)

        this.uploadSuccess(attachment)
        await feedStore.refresh()

        return attachment
      } catch (error) {
        if (error instanceof Error) {
          console.error(error.message)
        }
        throw error
      }
    },
    /**
     * Validate multiple files, add the accepted files to the attachments list in a pending state, then upload them.
     *
     * @param files The files to upload
     * @param meta Metadata for the attachment
     * @param fuzzy Whether to save as fuzzy, supported for Django programs only
     * @param validate Whether to validate the file against existing attachments
     */
    async bulkUpload ({ files, meta, fuzzy, validate }: {
      files: File[]
      meta: UploadMeta
      fuzzy?: boolean
      validate?: boolean
    }): Promise<AttachmentDetail[]> {
      const notificationStore = useNotificationStore()
      const pendingIds = new Set<string>()

      try {
        const validatedFiles: Array<{ file: File, pendingId: string }> = []
        for (const file of files) {
          if (!validate || await this.validate({ file, policyDocument: meta.policyDocument })) {
            const { id: pendingId } = this.uploadPending(file, meta)
            validatedFiles.push({ file, pendingId })
            pendingIds.add(pendingId)
          }
        }
        const uploadedFiles = await Promise.all(validatedFiles.map(async ({ file, pendingId }) => {
          try {
            return await this.upload({ file, meta, fuzzy })
          } catch (error) {
            notificationStore.snackbar.error(`Failed to upload file "${file.name}"`)
            return undefined
          } finally {
            const index = this.allPendingAttachments.findIndex(({ id }) => id === pendingId)
            if (index !== -1) this.allPendingAttachments.splice(index, 1)
          }
        }))
        return uploadedFiles.filter((attachment) => attachment !== undefined)
      } catch (error) {
        this.allPendingAttachments = this.allPendingAttachments.filter(({ id }) => !pendingIds.has(id))
        throw error
      }
    },
    confirmSignature ({ luid, confirmed }: { luid: string, confirmed: boolean }): ReturnType<typeof api.attachments.confirm_signature> {
      return api.attachments.confirm_signature({ path: { luid }, body: { confirmed } })
    },
    /**
     * Stages an attachment that only exists in the browser until its upload completes.
     */
    uploadPending (file: File, meta: UploadMeta): AttachmentDetail {
      const attachment = AttachmentDetail.create({
        id: `${ATTACHMENT_UPLOAD_PREFIX}${generateUUID()}`,
        applicationId: meta.applicationId,
        policyDocument: meta.policyDocument,
        visibility: meta.visibility,
        group: meta.group,
        filename: file.name,
        mimeType: file.type,
        sizeBytes: String(file.size),
        category: typeof meta.category === 'string'
          ? stringToEnumAttachmentCategory(meta.category)
          : meta.category,
      })
      attachment[ATTACHMENT_PENDING_KEY] = true
      this.allPendingAttachments.push(attachment)
      return attachment
    },
    uploadSuccess (attachment: AttachmentDetail): void {
      this.allApplicationAttachments.unshift(attachment)
      this.allPolicyAttachments.unshift(attachment)
    },
    removeFile (attachmentId: string | number): void {
      [this.allApplicationAttachments, this.allPolicyAttachments].forEach((attachments) => {
        const index = attachments.findIndex((f) => f.id === String(attachmentId))
        if (index === -1) return
        revokeAttachmentContent(attachments[index]!)
        attachments.splice(index, 1)
      })
    },
    /**
     * Replaces an attachment in every list it appears in, preserving content URLs.
     */
    replaceAttachment (attachment: AttachmentDetail): void {
      [this.allApplicationAttachments, this.allPolicyAttachments].forEach((attachments) => {
        const index = attachments.findIndex((f) => f.id === attachment.id)
        if (index !== -1) attachments.splice(index, 1, preserveAttachmentContent(attachment, attachments[index]))
      })
    },
  },
})

hotReloadStore(useAttachmentStore)
